export {
  PDFArray,
  PDFDictionary,
  PDFName,
  PDFNumber,
  PDFRaw,
  PDFRef,
  PDFStream,
  PDFString,
  serializePdfObject,
} from "./pdf-objects.js";

export { writePdfDocument } from "./pdf-writer.js";
export { PdfEngine } from "./engine.js";
export { validatePdfDocument } from "./engine.js";
import { validatePdfDocumentSafe as _validatePdfDocumentSafe } from "./validate-document.js";
export type {
  PdfValidationIssue,
  PdfValidationIssueCode,
  PdfValidationResult,
} from "./validate-document.js";
export { parseColor, tryParseColor, PdfColorParseError } from "./parse-color.js";
export type { PdfColorInput } from "./parse-color.js";
export { renderPdfPages } from "./pdf-renderer.js";
export type { PdfTextEncodingWarning } from "./pdf-renderer.js";
export { SRGB_ICC_PROFILE } from "./pdfa/srgb-icc-profile.js";
export { analyzePhase6Document } from "./phase6-analyze.js";
export { analyzePhase7Document } from "./phase7-analyze.js";
export { analyzePhase8Document } from "./phase8-analyze.js";
export { analyzePdfCapabilities, planPdfCapabilities } from "./capability-planner.js";
export {
  PDF_RELAXED_INPUT_COERCIONS,
  preprocessPdfDocumentInput,
} from "./relaxed-input.js";
export {
  PdfBinarySourceSchema,
  PdfDocumentPhase2Schema,
  PdfDocumentSchema,
  PdfEmbeddedFontInputSchema,
  PdfStructuredDocumentSchema,
} from "./schema.js";
import { hasPdfProLicense, requirePdfPro } from "./pro-guard.js";
import { linearizePdfBuffer as _linearizePdfBuffer } from "./phase9-stream.js";
import { extractPdfSignatures as _extractPdfSignatures } from "./phase10-validate.js";
import { validatePdfBuffer as _validatePdfBuffer } from "./phase10-validate.js";
import { buildPdfQualityReport as _buildPdfQualityReport } from "./phase10-quality.js";
import {
  repairPdfBuffer as _repairPdfBuffer,
  validateAndRepairPdfBuffer as _validateAndRepairPdfBuffer,
} from "./phase10-repair.js";

export { hasPdfProLicense } from "./pro-guard.js";

export function linearizePdfBuffer(...args: Parameters<typeof _linearizePdfBuffer>) {
  requirePdfPro("streaming-generation");
  return _linearizePdfBuffer(...args);
}

export function extractPdfSignatures(...args: Parameters<typeof _extractPdfSignatures>) {
  requirePdfPro("digital-signatures");
  return _extractPdfSignatures(...args);
}

export function buildPdfQualityReport(...args: Parameters<typeof _buildPdfQualityReport>) {
  requirePdfPro("PDF quality inspection");
  return _buildPdfQualityReport(...args);
}
export { buildSharedPdfQualityReport } from "./shared-quality.js";

export type {
  PdfExistingFormFieldType,
  PdfFillExistingFormOptions,
  PdfFillExistingFormResult,
  PdfFormFieldInfo,
  PdfFormFillWarning,
  PdfFormInspection,
  PdfFormValue,
} from "./pdf-form-fill.js";
export type {
  FindingCode,
  QualityFinding,
  QualityReport,
  QualityVerdict,
  RenderWithQualityResult,
  RepairEntry,
  RepairRisk,
} from "./public-quality-types.js";
export function repairPdfBuffer(...args: Parameters<typeof _repairPdfBuffer>) {
  requirePdfPro("PDF repair");
  return _repairPdfBuffer(...args);
}

export function validateAndRepairPdfBuffer(...args: Parameters<typeof _validateAndRepairPdfBuffer>) {
  requirePdfPro("PDF repair");
  return _validateAndRepairPdfBuffer(...args);
}

export function validatePdfBuffer(...args: Parameters<typeof _validatePdfBuffer>) {
  requirePdfPro("PDF validation");
  return _validatePdfBuffer(...args);
}

export function validatePdfDocumentSafe(
  ...args: Parameters<typeof _validatePdfDocumentSafe>
) {
  return _validatePdfDocumentSafe(...args);
}
export { PdfError, isPdfError } from "./errors.js";
export type { PdfErrorCode, PdfErrorDetails } from "./errors.js";
export { setDeterministicMode, isDeterministicModeEnabled } from "./deterministic-mode.js";
export type { PdfAssetPolicy, PdfRenderTrace, PdfVersion } from "./phase9-types.js";
export type {
  PdfCapabilities,
  PdfCapabilityPlan,
  PdfSelectedPhase,
} from "./capability-planner.js";
export type { PdfEmbeddedFontInput, PdfFontInput } from "./font-embedding.js";
export type { PdfInputWarning, PdfRelaxedInputOptions } from "./relaxed-input.js";

export type {
  PDFDictionaryEntries,
  PDFIndirectObject,
  PDFValue,
} from "./pdf-objects.js";

export type {
  PdfDocument,
  PdfDocumentPhase6,
  PdfDocumentPhase7,
  PdfDocumentPhase8,
  PdfDocumentPhase2,
  PdfDocumentPhase5,
  PdfPagePhase2,
  PdfRenderOptions,
  PdfTextPhase2,
} from "./engine.js";

export type {
  PdfCertificateSource,
  PdfComplianceLevel,
  PdfExtractedSignature,
  PdfFindingCategory,
  PdfFindingCode,
  PdfFindingSeverity,
  PdfP12CertificateSource,
  PdfPemCertificateSource,
  PdfQualityFinding,
  PdfQualityReport,
  PdfQualityVerdict,
  PdfRepairAction,
  PdfRepairOptions,
  PdfRepairResult,
  PdfRepairValidationResult,
  PdfSignOptions,
  PdfTimestampAuthorityOptions,
  PdfValidationCheck,
  PdfValidationSummary,
  PdfValidationVerdict,
} from "./phase10-types.js";

export type {
  PdfDocumentPhase3,
  PdfDocumentLayoutNode,
  PdfPhase3ContainerNode,
  PdfPhase3HeadingNode,
  PdfPhase3InternalLink,
  PdfPhase3Link,
  PdfPhase3ExternalLink,
  PdfPhase3Margins,
  PdfPhase3Node,
  PdfPhase3Page,
  PdfPhase3ParagraphNode,
  PdfPhase3PreformattedNode,
  PdfPhase3Size,
  PdfPhase3Style,
  PdfPhase3TextBase,
  PdfPhase3WidowOrphan,
} from "./phase3-types.js";

export type {
  PdfPhase5Border,
  PdfPhase5CellContentNode,
  PdfPhase5CellStyle,
  PdfPhase5RowStyle,
  PdfPhase5TableCell,
  PdfPhase5TableColumn,
  PdfPhase5TableNode,
  PdfPhase5TableStyle,
  PdfPhase5TableRow,
} from "./phase5-types.js";

export type {
  PdfBinarySource,
  PdfColor,
  PdfFill,
  PdfGradientStop,
  PdfGraphic,
  PdfImageGraphic,
  PdfLineGraphic,
  PdfLinearGradientFill,
  PdfPathGraphic,
  PdfRadialGradientFill,
  PdfRectGraphic,
  PdfSolidFill,
  PdfStrokeStyle,
  PdfSvgGraphic,
} from "./phase4-types.js";

export type {
  PdfAccessibilityStructureSpec,
  PdfDocumentAccessibilitySpec,
  PdfDocumentInteractiveSpec,
  PdfExternalLinkAnnotationSpec,
  PdfInternalLinkAnnotationSpec,
  PdfMarkedContentSpec,
  PdfPageAnnotationSpec,
  PdfPageExtraCommand,
  PdfRenderedPage,
  PdfRenderMeta,
  PdfRenderableGraphic,
  PdfRenderableText,
  PdfSignatureWidgetSpec,
  PdfTextAnnotationSpec,
  PdfTransformMatrix,
} from "./pdf-renderer.js";

export type {
  PdfDocumentPhase6 as PdfPhase6Document,
  PdfDynamicFooterOptions,
  PdfDynamicHeaderFooterConfiguredContent,
  PdfDynamicHeaderFooterContent,
  PdfDynamicHeaderFooterOptions,
  PdfDynamicHeaderFooterZones,
  PdfDynamicHeaderOptions,
  PdfPhase6BookmarkOptions,
  PdfPhase6CheckboxNode,
  PdfPhase6DocumentNode,
  PdfPhase6DropdownNode,
  PdfPhase6HighlightAnnotationNode,
  PdfPhase6NoteAnnotationNode,
  PdfPhase6PageLabel,
  PdfPhase6PageNumberOptions,
  PdfPhase6RadioButtonNode,
  PdfPhase6SignatureFieldNode,
  PdfPhase6TextFieldNode,
  PdfPhase6TocNode,
} from "./phase6-types.js";

export type {
  PdfDocumentPhase7 as PdfPhase7Document,
  PdfPhase7AccessibilityOptions,
  PdfPhase7FigureNode,
  PdfPhase7ListItemNode,
  PdfPhase7ListNode,
} from "./phase7-types.js";

export type {
  PdfaConformanceLevel,
  PdfDocumentPhase8 as PdfPhase8Document,
  PdfPhase8PdfaOptions,
} from "./phase8-types.js";

export type {
  PdfEncryptionAlgorithm,
  PdfEncryptionConfig,
  PdfPermissionFlags,
} from "./encryption/types.js";
